import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * エリア選択集計 PDF 生成 utility (2026-07-16、`apps/user/src/features/insights/
 * generateIdeaPdf.ts` の section 単位 html2canvas + jsPDF 積み上げ方式を踏襲、簡略版)。
 *
 * `generateIdeaPdf.ts` との違い:
 *   - 写真解決 (`resolvePhotoUrl`) が不要 (このレポートに写真は無い)。
 *   - 地図キャプチャは呼出側 (`AreaReportPreviewMap.capture()`) が既に済ませた dataURL を
 *     受け取るだけ (`mapElement` を html2canvas で撮る方式ではない — WebGL canvas の
 *     html2canvas 撮影は不安定なため、ネイティブ `getCanvas().toDataURL()` の結果を使う)。
 *   - `.pdf-section` が必ず存在する構造のため、旧の「section 0件フォールバック」分岐は省略。
 */

export interface GenerateAreaReportPdfOptions {
  /** `AreaReportPdfTemplate` がレンダした隠し DOM の root 要素。 */
  templateRef: HTMLDivElement;
  fileName: string;
  /** `AreaReportPreviewMap.capture()` の結果。null/undefined なら地図は空欄のまま。 */
  mapDataUrl?: string | null;
}

export async function generateAreaReportPdf(opts: GenerateAreaReportPdfOptions): Promise<void> {
  const { templateRef, fileName, mapDataUrl } = opts;

  if (mapDataUrl) {
    const mapImg = templateRef.querySelector<HTMLImageElement>('img[alt="地図"]');
    if (mapImg) mapImg.src = mapDataUrl;
  }

  // 全 img の load 完了を待つ (gray box / 抜け防止、generateIdeaPdf.ts 踏襲)。
  const images = Array.from(templateRef.querySelectorAll('img'));
  await Promise.all(
    images.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
        setTimeout(() => resolve(), 3000);
      });
    }),
  );
  await new Promise((r) => setTimeout(r, 300));

  const elementsToCapture = Array.from(templateRef.querySelectorAll<HTMLElement>('.pdf-section'));

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const contentHeightOnPage = pdfHeight - margin * 2;

  let currentY = margin;
  let isFirstPage = true;

  for (const el of elementsToCapture) {
    const canvas = await html2canvas(el, {
      useCORS: true,
      allowTaint: true,
      scale: 2,
      logging: false,
      width: 794,
      windowWidth: 794,
    });
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    if (currentY + imgHeight > pdfHeight - margin) {
      if (imgHeight > contentHeightOnPage) {
        // 1 page 超のジャンボ section: 分割して複数 page にまたがらせる。
        if (!isFirstPage || currentY > margin) {
          pdf.addPage();
          currentY = margin;
        }
        let heightLeft = imgHeight;
        pdf.addImage(imgData, 'PNG', 0, currentY, imgWidth, imgHeight);
        heightLeft -= pdfHeight - currentY - margin;
        currentY += imgHeight;
        while (heightLeft > 0) {
          const position = heightLeft - imgHeight + margin;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= contentHeightOnPage;
        }
        currentY = margin + heightLeft;
      } else {
        if (!isFirstPage || currentY > margin) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, margin, imgWidth, imgHeight);
        currentY = margin + imgHeight;
      }
    } else {
      pdf.addImage(imgData, 'PNG', 0, currentY, imgWidth, imgHeight);
      currentY += imgHeight;
    }

    isFirstPage = false;
  }

  pdf.save(fileName);
}
